import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Instagram } from 'lucide-react';
import { BRAND, TOUR_BANNER } from '../lib/content';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/rates', label: 'Rates' },
  { to: '/etiquette', label: 'Etiquette' },
  { to: '/tour', label: 'Tour' },
  { to: '/testimonials', label: 'Testimonials' },
  { to: '/contact', label: 'Book' },
];

function TourBanner() {
  return (
    <div
      data-testid="tour-banner"
      className="w-full bg-zb-ink text-zb-bg overflow-hidden"
    >
      <div className="zb-marquee-track py-2.5 font-label text-[10.5px]">
        {[0, 1].map((i) => (
          <span key={i} className="inline-flex items-center gap-3">
            <span aria-hidden>◆</span>
            <span>{TOUR_BANNER}</span>
            <span aria-hidden>◆</span>
            <Link
              to="/tour"
              className="underline underline-offset-4 hover:text-zb-bg2"
              data-testid={`tour-banner-link-${i}`}
            >
              View full tour
            </Link>
          </span>
        ))}
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-zb-bg/80 border-b border-zb-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-serif text-2xl md:text-3xl text-zb-ink tracking-tight"
        >
          Zahra <span className="italic text-zb-brand">Belle</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-label text-[11px] transition-colors ${
                  isActive ? 'text-zb-brand' : 'text-zb-ink2 hover:text-zb-ink'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-book"
            className="rounded-full bg-zb-brand hover:bg-zb-brandHover text-zb-bg px-5 py-2 text-[11px] tracking-[0.22em] uppercase transition-colors"
          >
            Request
          </Link>
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
          className="md:hidden text-zb-ink p-2"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-zb-border bg-zb-bg">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-serif text-2xl ${
                    isActive ? 'text-zb-brand' : 'text-zb-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-12 md:mt-16 border-t border-zb-border bg-zb-bg2">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 grid gap-10 md:grid-cols-3 items-start">
        {/* Stay in touch — left */}
        <div>
          <div className="font-label text-zb-accent mb-5 text-xs tracking-[0.24em]">Stay in touch</div>
          <a
            data-testid="footer-instagram-link"
            href={BRAND.instagram}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2.5 text-zb-ink hover:text-zb-brand transition-colors font-medium text-base"
          >
            <Instagram size={18} /> {BRAND.instagramHandle}
          </a>
          <div className="mt-3">
            <a
              data-testid="footer-email-link"
              href={`mailto:${BRAND.email}`}
              className="text-zb-ink hover:text-zb-brand transition-colors font-medium text-base"
            >
              {BRAND.email}
            </a>
          </div>
          <div className="mt-3 text-zb-ink font-medium text-base">
            SMS ·{' '}
            <a
              data-testid="footer-sms-link"
              href={`sms:${BRAND.phoneIntl.replace(/\s/g, '')}`}
              className="hover:text-zb-brand transition-colors"
            >
              {BRAND.phoneIntl}
            </a>
          </div>
          <div className="mt-3">
            <a
              data-testid="footer-whatsapp-link"
              href={BRAND.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="text-zb-ink hover:text-zb-brand transition-colors font-medium text-base"
            >
              WhatsApp · {BRAND.phoneIntl}
            </a>
          </div>
        </div>

        {/* Location — middle */}
        <div>
          <div className="font-label text-zb-accent mb-5 text-xs tracking-[0.24em]">Where</div>
          <div className="font-serif text-2xl md:text-3xl text-zb-ink leading-snug">
            Based in Melbourne
          </div>
          <div className="mt-2 text-zb-ink2 text-base">
            Available worldwide, by invitation.
          </div>
        </div>

        {/* Ready — right */}
        <div className="md:text-right">
          <div className="font-label text-zb-accent mb-5 text-xs tracking-[0.24em]">Ready?</div>
          <Link
            to="/contact"
            data-testid="footer-cta-book"
            className="inline-block rounded-full bg-zb-ink text-zb-bg px-7 py-3.5 text-xs tracking-[0.28em] uppercase font-semibold hover:bg-zb-brand transition-colors"
          >
            Let&rsquo;s meet
          </Link>
        </div>
      </div>

      {/* Bottom bar — Zahra Belle tagline in one line */}
      <div className="border-t border-zb-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="font-serif text-2xl md:text-3xl text-zb-ink">
            Zahra <span className="italic text-zb-brand">Belle</span>
            <span className="ml-4 md:ml-5 font-label text-zb-accent text-xs tracking-[0.28em] uppercase align-middle">
              {BRAND.tagline}
            </span>
          </div>
          <div className="flex items-center gap-6 font-label text-[11px] text-zb-accent tracking-[0.28em] uppercase">
            <span>© {new Date().getFullYear()}</span>
            <button
              data-testid="footer-back-to-cover"
              onClick={() => {
                sessionStorage.removeItem('zb_age_ok');
                localStorage.removeItem('zb_age_ok');
                window.location.href = '/';
              }}
              className="hover:text-zb-brand transition-colors underline underline-offset-4"
            >
              Back to cover
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-zb-bg text-zb-ink">
      <TourBanner />
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
